import React, { useState } from 'react';

interface Message {
    id: string;
    content: string;
    isUser: boolean;
    timestamp: Date;
}

interface ChatbotPreviewProps {
    chatbotName: string;
    primaryColor: string;
    theme: 'light' | 'dark' | 'system';
}

const ChatbotPreview: React.FC<ChatbotPreviewProps> = ({
                                                           chatbotName,
                                                           primaryColor = '#3b82f6',
                                                           theme = 'light'
                                                       }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            content: `Hallo! Ich bin ${chatbotName}. Wie kann ich heute helfen?`,
            isUser: false,
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');

    const handleSendMessage = () => {
        if (!inputValue.trim()) return;

        // Nutzer-Nachricht hinzufügen
        const userMessage: Message = {
            id: Date.now().toString(),
            content: inputValue,
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prevMessages => [...prevMessages, userMessage]);
        setInputValue('');

        // Simulierte Bot-Antwort
        setTimeout(() => {
            let botResponse = '';
            const userInput = inputValue.toLowerCase();

            if (userInput.includes('hallo') || userInput.includes('hi')) {
                botResponse = 'Hallo! Wie kann ich dir helfen?';
            } else if (userInput.includes('hilfe') || userInput.includes('unterstützung')) {
                botResponse = 'Ich bin hier, um bei Fragen zu helfen. Was möchtest du wissen?';
            } else if (userInput.includes('öffnungszeiten')) {
                botResponse = 'Wir haben Montag bis Freitag von 9 bis 18 Uhr geöffnet.';
            } else if (userInput.includes('kontakt')) {
                botResponse = 'Sie können uns unter kontakt@example.com oder telefonisch unter +49 123 456789 erreichen.';
            } else {
                botResponse = 'Danke für deine Nachricht! Wie kann ich dir weiterhelfen?';
            }

            const botMessage: Message = {
                id: Date.now().toString(),
                content: botResponse,
                isUser: false,
                timestamp: new Date()
            };

            setMessages(prevMessages => [...prevMessages, botMessage]);
        }, 1000);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    const bgColor = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
    const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
    const secondaryTextColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-500';
    const borderColor = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';

    return (
        <div className="fixed bottom-5 right-5">
            {isOpen ? (
                <div className={`${bgColor} shadow-xl rounded-lg w-80 md:w-96 overflow-hidden border ${borderColor}`}>
                    {/* Header */}
                    <div className="flex items-center justify-between p-4" style={{ backgroundColor: primaryColor }}>
                        <h2 className="font-medium text-white">{chatbotName}</h2>
                        <button
                            onClick={toggleChat}
                            className="text-white hover:text-gray-200"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    {/* Messages */}
                    <div className={`h-80 p-4 overflow-y-auto ${bgColor}`}>
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`mb-3 flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`px-4 py-2 rounded-lg max-w-xs md:max-w-sm ${
                                        message.isUser
                                            ? `text-white rounded-br-none`
                                            : `${bgColor} border ${borderColor} rounded-bl-none`
                                    }`}
                                    style={{ backgroundColor: message.isUser ? primaryColor : '' }}
                                >
                                    <p className={`${message.isUser ? 'text-white' : textColor}`}>
                                        {message.content}
                                    </p>
                                    <p className={`text-xs mt-1 ${message.isUser ? 'text-white text-opacity-75' : secondaryTextColor}`}>
                                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Input */}
                    <div className={`p-4 border-t ${borderColor} ${bgColor}`}>
                        <div className="flex items-center">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Schreib eine Nachricht..."
                                className={`flex-1 p-2 rounded-l-md border ${borderColor} focus:outline-none focus:ring-2 ${theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                            />
                            <button
                                onClick={handleSendMessage}
                                className="p-2 rounded-r-md text-white"
                                style={{ backgroundColor: primaryColor }}
                                disabled={!inputValue.trim()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <button
                    onClick={toggleChat}
                    className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: primaryColor }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default ChatbotPreview;